// src/auth/auth-screen-shell.tsx
//
// The shared centered-card chrome for every unauthenticated auth surface
// (login + the recovery suite). Extracted from login-page's layout so the
// recovery screens compose the SAME container/eyebrow/heading instead of each
// re-declaring the token values — one source of truth for the sign-in visual.
// Colour/spacing/radius/shadow are DS tokens ONLY (no raw hex).
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ScripsWordmark } from '@/app/scrips-wordmark';

/** The sign-in card's brand line and title, shared by the login page and the recovery screens (consistency pass,
 *  15 Oct): the Scrips wordmark in the app's brand colour with the product name beside it in the meta role, then the
 *  title in the title role (24/32/600). Was a bold blue uppercase eyebrow and a 20/400 heading. */
export function AuthBrandHeading({ product = 'Practitioner', heading }: { product?: string; heading: string }) {
  return (
    <>
      <p className="m-0 flex items-center gap-2 text-xs text-text-secondary">
        <span className="inline-flex text-brand-primary">
          <ScripsWordmark height={20} />
        </span>
        {product}
      </p>
      <h1 className="m-0 text-lg font-semibold text-text-primary">{heading}</h1>
    </>
  );
}

interface AuthScreenShellProps {
  /** The product name beside the wordmark (defaults to "Practitioner"). */
  eyebrow?: string;
  heading: string;
  /** Optional supporting copy under the heading. */
  intro?: ReactNode;
  children: ReactNode;
  /** Footer row (e.g. "Back to sign in" link). */
  footer?: ReactNode;
}

export function AuthScreenShell({
  eyebrow = 'Practitioner',
  heading,
  intro,
  children,
  footer,
}: AuthScreenShellProps) {
  return (
    <main className="grid min-h-app-screen place-items-center bg-surface-background p-4 font-sans">
      {/* The one card: 12 corners, hairline, white, no shadow (shadows are for overlays). */}
      <div className="flex w-90 max-w-full flex-col gap-4 rounded-lg border border-border-default bg-surface-card p-8">
        <AuthBrandHeading product={eyebrow} heading={heading} />
        {intro && (
          <p className="m-0 text-sm text-text-secondary">
            {intro}
          </p>
        )}
        {children}
        {footer && (
          <div className="mt-1 flex justify-center">
            {footer}
          </div>
        )}
      </div>
    </main>
  );
}

/** A text button styled as an inline link using DS tokens — used for the
 *  "Forgot password?", "Back to sign in", and "Resend code" affordances. No raw
 *  hex; colour is the brand-primary token. */
export function AuthLinkButton({
  onClick,
  children,
  disabled,
}: {
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'm-0 border-none bg-none p-0 font-sans text-sm font-medium no-underline',
        disabled
          ? 'cursor-default text-text-muted'
          : 'cursor-pointer text-brand-primary',
      )}
    >
      {children}
    </button>
  );
}
